import { Check, Loader2 } from 'lucide-react'
import {
  COMPANY_SIZES,
  PRIMARY_USE_CASES,
  passwordStrength,
  signupStepSchemas,
} from '@insightai/auth'
import {
  Button,
  Checkbox,
  Field,
  Input,
  RadioGroup,
  Stepper,
  cn,
  useToast,
  type StepDefinition,
} from '@insightai/ui'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { config } from '../../config'
import { prototypeEmailStepSchema } from '../../auth/mockAuth'
import { startPkce } from '../../auth/startPkce'
import { useDomainValidation } from '../../auth/useDomainValidation'
import { AuthLayout } from './AuthLayout'
import { PrototypeNotice } from './PrototypeNotice'
import { SocialButtons } from './SocialButtons'

type StepId = 'email' | 'company' | 'useCase' | 'password'

/**
 * Step 1 drops the work-email rule in prototype mode so a dummy address — most
 * likely a personal one — gets through. Chosen here rather than in
 * @insightai/auth, which the console shares.
 */
const emailStepSchema = config.prototypeMode ? prototypeEmailStepSchema : signupStepSchemas.email

const STEPS: StepDefinition[] = [
  { id: 'email', label: 'Work email' },
  { id: 'company', label: 'About you' },
  { id: 'useCase', label: 'What you research' },
  { id: 'password', label: 'Secure your account' },
]

/**
 * M00-17 — multi-step signup: work email → company size → primary use case → password.
 *
 * Design decisions worth recording:
 *   · Each step validates with its own Zod schema, so an error on step 4 never
 *     re-surfaces step 1's messages.
 *   · Back-navigation preserves every field. The same rule as BB-FR-001: a form
 *     that forgets on Back is the fastest way to lose a signup.
 *   · The password step's requirement checklist is live and per-rule rather than a
 *     single "invalid password" message — a strength bar alone tells a user they
 *     failed but not what to change.
 *   · The account is created by Keycloak, so the final step hands off to the
 *     registration flow with the collected profile carried as a hint. No password
 *     ever posts to our own API.
 */
export function SignupPage() {
  const toast = useToast()
  const [currentStep, setCurrentStep] = useState<StepId>('email')
  const [completed, setCompleted] = useState<Set<StepId>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [form, setForm] = useState({
    email: '',
    fullName: '',
    companySize: '',
    primaryUseCase: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    marketingOptIn: false,
  })

  const domain = useDomainValidation(form.email)
  const strength = useMemo(() => passwordStrength(form.password), [form.password])

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((current) => ({ ...current, [key]: value }))
    setErrors((current) => {
      const next = { ...current }
      delete next[key as string]
      return next
    })
  }

  function validateStep(step: StepId): boolean {
    const result =
      step === 'email'
        ? emailStepSchema.safeParse({ email: form.email })
        : step === 'company'
          ? signupStepSchemas.company.safeParse({
              companySize: form.companySize,
              fullName: form.fullName,
            })
          : step === 'useCase'
            ? signupStepSchemas.useCase.safeParse({ primaryUseCase: form.primaryUseCase })
            : signupStepSchemas.password.safeParse({
                password: form.password,
                confirmPassword: form.confirmPassword,
                acceptTerms: form.acceptTerms,
              })

    if (result.success) {
      setErrors({})
      return true
    }

    const fieldErrors: Record<string, string> = {}
    for (const issue of result.error.issues) {
      const key = String(issue.path[0] ?? 'form')
      fieldErrors[key] ??= issue.message
    }
    setErrors(fieldErrors)
    return false
  }

  const order: StepId[] = ['email', 'company', 'useCase', 'password']

  function next() {
    if (!validateStep(currentStep)) return
    if (currentStep === 'email' && domain.state.status === 'invalid') return

    setCompleted((current) => new Set(current).add(currentStep))
    const index = order.indexOf(currentStep)
    if (index < order.length - 1) setCurrentStep(order[index + 1]!)
  }

  async function submit() {
    if (!validateStep('password')) return
    setSubmitting(true)
    try {
      await startPkce({ register: true, loginHint: form.email })
    } catch {
      setSubmitting(false)
      toast({
        title: "We couldn't start account creation",
        description: 'Check your connection and try again.',
        tone: 'critical',
      })
    }
  }

  return (
    <AuthLayout
      title="Create your workspace"
      subtitle="Start with a free brief — no methodology expertise required"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/auth/login" className="text-text-link underline underline-offset-4">
            Sign in
          </Link>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Stepper
          steps={STEPS.map((step) => ({ ...step, complete: completed.has(step.id as StepId) }))}
          currentStepId={currentStep}
          onStepChange={(id) => setCurrentStep(id as StepId)}
          label="Signup progress"
          orientation="vertical"
          className="rounded-md border border-border-subtle p-1"
        />

        {currentStep === 'email' && (
          <div className="flex flex-col gap-5">
            <PrototypeNotice />
            <SocialButtons mode="signup" />
            <div className="flex items-center gap-3" aria-hidden>
              <span className="h-px flex-1 bg-border-subtle" />
              <span className="text-caption text-text-tertiary">or use your work email</span>
              <span className="h-px flex-1 bg-border-subtle" />
            </div>

            <Field
              label="Work email"
              required
              hint="We create your workspace from your company domain."
              error={
                errors.email ??
                (domain.state.status === 'invalid' ? domain.state.message : undefined)
              }
            >
              <Input
                type="email"
                autoComplete="email"
                autoFocus
                value={form.email}
                onChange={(event) => set('email', event.target.value)}
                onBlur={domain.checkNow}
                iconEnd={
                  domain.state.status === 'checking' ? (
                    <Loader2 aria-hidden className="size-4 animate-spin" />
                  ) : domain.state.status === 'valid' ? (
                    <Check aria-hidden className="size-4 text-state-verified" />
                  ) : undefined
                }
              />
            </Field>

            {/* Live region: the domain verdict arrives asynchronously and must be
                announced, not only painted. */}
            <p aria-live="polite" className="text-caption text-text-secondary">
              {domain.state.status === 'checking' && 'Checking your domain…'}
              {domain.state.status === 'valid' &&
                (domain.state.existingWorkspace
                  ? `${domain.state.existingWorkspace} already uses InsightAI — you'll be added to it.`
                  : 'Domain looks good.')}
            </p>
          </div>
        )}

        {currentStep === 'company' && (
          <div className="flex flex-col gap-4">
            <Field label="Your name" required error={errors.fullName}>
              <Input
                autoComplete="name"
                autoFocus
                value={form.fullName}
                onChange={(event) => set('fullName', event.target.value)}
              />
            </Field>
            <Field label="Company size" required error={errors.companySize}>
              <RadioGroup
                label="Company size"
                options={COMPANY_SIZES.map((size) => ({ value: size.value, label: size.label }))}
                value={form.companySize}
                onValueChange={(value) => set('companySize', value)}
              />
            </Field>
          </div>
        )}

        {currentStep === 'useCase' && (
          <Field label="What will you research first?" required error={errors.primaryUseCase}>
            <RadioGroup
              label="Primary use case"
              options={PRIMARY_USE_CASES.map((useCase) => ({
                value: useCase.value,
                label: useCase.label,
              }))}
              value={form.primaryUseCase}
              onValueChange={(value) => set('primaryUseCase', value)}
            />
          </Field>
        )}

        {currentStep === 'password' && (
          <div className="flex flex-col gap-4">
            <Field label="Password" required error={errors.password}>
              <Input
                type="password"
                autoComplete="new-password"
                autoFocus
                value={form.password}
                onChange={(event) => set('password', event.target.value)}
              />
            </Field>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div
                  role="meter"
                  aria-label="Password strength"
                  aria-valuenow={strength.score}
                  aria-valuemin={0}
                  aria-valuemax={4}
                  aria-valuetext={strength.label}
                  className="flex h-1.5 flex-1 gap-1"
                >
                  {[0, 1, 2, 3].map((index) => (
                    <span
                      key={index}
                      className={cn(
                        'h-full flex-1 rounded-full',
                        index < strength.score
                          ? strength.score >= 3
                            ? 'bg-state-verified'
                            : 'bg-state-warning'
                          : 'bg-surface-inset',
                      )}
                    />
                  ))}
                </div>
                <span className="text-caption text-text-secondary">{strength.label}</span>
              </div>

              <ul className="flex flex-col gap-0.5">
                {(
                  [
                    ['length', 'At least 12 characters'],
                    ['lower', 'A lowercase letter'],
                    ['upper', 'An uppercase letter'],
                    ['digit', 'A number'],
                    ['symbol', 'A symbol'],
                  ] as const
                ).map(([key, label]) => (
                  <li
                    key={key}
                    className={cn(
                      'flex items-center gap-1.5 text-caption',
                      strength.met[key] ? 'text-state-verified' : 'text-text-tertiary',
                    )}
                  >
                    <Check aria-hidden className={cn('size-3', !strength.met[key] && 'opacity-30')} />
                    {label}
                    <span className="sr-only">{strength.met[key] ? '— met' : '— not yet met'}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Field label="Confirm password" required error={errors.confirmPassword}>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(event) => set('confirmPassword', event.target.value)}
              />
            </Field>

            <Checkbox
              label={
                <>
                  I accept the{' '}
                  <Link to="/legal/terms" className="text-text-link underline underline-offset-4">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/legal/privacy" className="text-text-link underline underline-offset-4">
                    Privacy Policy
                  </Link>
                </>
              }
              checked={form.acceptTerms}
              onCheckedChange={(checked) => set('acceptTerms', checked)}
            />
            {errors.acceptTerms && (
              <p role="alert" className="text-caption text-state-critical">
                {errors.acceptTerms}
              </p>
            )}

            <Checkbox
              label="Send me occasional product updates"
              checked={form.marketingOptIn}
              onCheckedChange={(checked) => set('marketingOptIn', checked)}
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          {currentStep !== 'email' && (
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(order[Math.max(0, order.indexOf(currentStep) - 1)]!)}
            >
              Back
            </Button>
          )}
          {currentStep === 'password' ? (
            <Button
              className="ms-auto"
              onClick={() => void submit()}
              loading={submitting}
              loadingLabel="Creating your account"
            >
              Create account
            </Button>
          ) : (
            <Button className="ms-auto" onClick={next}>
              Continue
            </Button>
          )}
        </div>

        <p className="text-caption text-text-tertiary">
          {config.prototypeMode
            ? 'No account is created in prototype mode. Creating one signs you straight into the console with fixture data.'
            : `Your account is created through our identity provider at ${new URL(config.keycloak.authorizationEndpoint).host}. Your password is never sent to InsightAI's own servers.`}
        </p>
      </div>
    </AuthLayout>
  )
}
